import React, { useState } from 'react';
import { motion } from 'framer-motion';
import UserNavbar from '../components/UserNavbar';
import EnhancedDiscountSelector from '../components/EnhancedDiscountSelector';
import { FaShoppingCart, FaStore, FaMoneyBillWave } from 'react-icons/fa';

const DiscountDemo = () => {
  const [orderAmount, setOrderAmount] = useState(3000);
  const [deliveryFee, setDeliveryFee] = useState(600);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Mock order items
  const mockOrderItems = [
    { meal: 'meal_1', name: 'Jollof Rice', quantity: 2, price: 1500 },
    { meal: 'meal_2', name: 'Fried Chicken', quantity: 1, price: 1500 }
  ];

  const finalAmount = appliedDiscount 
    ? appliedDiscount.finalAmount 
    : orderAmount + deliveryFee;

  const savings = appliedDiscount ? appliedDiscount.savings : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-secondary-50">
      <UserNavbar />
      
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="container mx-auto px-4 py-6 pt-20 md:pt-24 pb-24 md:pb-8"
      >
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl font-bold text-crust font-display flex items-center justify-center gap-3 mb-2">
              <FaMoneyBillWave className="text-cheese" />
              Enhanced Discount System
            </h1>
            <p className="text-olive-light font-sans">Experience smart discount suggestions and real-time previews</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Mock Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-secondary-200"
            >
              <h2 className="text-xl font-bold text-crust font-display flex items-center gap-2 mb-4">
                <FaShoppingCart className="text-pepperoni" />
                Your Order
              </h2>

              <div className="space-y-3 mb-4">
                {mockOrderItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <div>
                      <h4 className="font-semibold text-crust">{item.name}</h4>
                      <p className="text-sm text-olive-light">Qty: {item.quantity}</p>
                    </div>
                    <span className="font-bold text-pepperoni">₦{item.price.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-sm border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₦{orderAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee:</span>
                  <span>₦{deliveryFee.toLocaleString()}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount Savings:</span>
                    <span>-₦{savings.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2">
                  <span>Total:</span>
                  <span className="text-pepperoni">₦{finalAmount.toLocaleString()}</span>
                </div>
              </div>

              {/* Order Controls */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-crust mb-2">
                    Order Amount (for testing):
                  </label>
                  <input
                    type="number"
                    value={orderAmount}
                    onChange={(e) => setOrderAmount(Number(e.target.value))}
                    className="w-full p-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-cheese"
                    min="500"
                    max="10000"
                    step="100"
                  />
                </div>

                <div className="flex items-center gap-2 text-sm text-olive-light">
                  <FaStore className="text-pepperoni" />
                  <span>Restaurant: Our Kitchen!</span>
                </div>
              </div>
            </motion.div>

            {/* Enhanced Discount Selector */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <EnhancedDiscountSelector
                orderAmount={orderAmount}
                restaurantId="6862cb35fd389cd879ac596c"
                deliveryFee={deliveryFee}
                orderItems={mockOrderItems}
                onDiscountApplied={(discountData) => {
                  setAppliedDiscount(discountData);
                }}
                onDiscountRemoved={() => {
                  setAppliedDiscount(null);
                }}
              />
            </motion.div>
          </div>

          {/* Features Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-12 bg-white rounded-2xl shadow-lg p-8 border border-secondary-200"
          >
            <h2 className="text-2xl font-bold text-crust font-display mb-6 text-center">
              🚀 Enhanced Discount Features
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-primary-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">👁️</span>
                </div>
                <h3 className="font-bold text-crust font-display mb-2">Real-time Preview</h3>
                <p className="text-sm text-olive-light">See exactly how much you'll save before applying any discount</p>
              </div>

              <div className="text-center">
                <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="font-bold text-crust font-display mb-2">Smart Suggestions</h3>
                <p className="text-sm text-olive-light">Get personalized discount recommendations based on your order</p>
              </div>

              <div className="text-center">
                <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="font-bold text-crust font-display mb-2">Instant Validation</h3>
                <p className="text-sm text-olive-light">Know immediately if a discount code is valid or expired</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gradient-to-r from-secondary-50 to-primary-50 rounded-xl border border-cheese">
              <h4 className="font-bold text-crust font-display mb-2">💡 Try These Features:</h4>
              <ul className="text-sm text-olive-light space-y-1">
                <li>• Click on suggested discounts to see instant previews</li>
                <li>• Type "SAVE20" or "FREEDEL" to test manual entry</li>
                <li>• Change the order amount to see different suggestions</li>
                <li>• Apply and remove discounts to see the smooth transitions</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </motion.main>
    </div>
  );
};

export default DiscountDemo;